import {
  BATCH_SIZES_COMBINED_OPTIONS,
  BATCH_SIZES_KG_OPTIONS,
  BATCH_SIZES_L_OPTIONS,
  BUDGET_RANGES_OPTIONS,
  CDMO_BUSINESS_PROFILE_OPTIONS,
  CLIENT_TYPES_OPTIONS,
  ENGAGEMENT_SCALES_OPTIONS,
  GMP_CERTIFICATIONS_OPTIONS,
  LEGACY_SUPPORT_SERVICE_ALIASES,
  OPERATING_REGIONS_OPTIONS,
  PRIMARY_CAPABILITIES_OPTIONS,
  PROJECT_TYPES_OPTIONS,
  SPEED_TO_START_OPTIONS,
  getDetailedCapabilityOptions,
  type CDMOBusinessProfile,
} from '../../constants';
import { resolveDetailedCapabilityLabel } from '../../constants/detailedCapabilities';
import { LEGACY_CERTIFICATION_ALIASES } from '../gatekeeperFilters';
import { normalizeWebsiteDomain } from '../cdmoApplicationRow';
import {
  validateCompany,
  validateEmail,
  validateEmailDomainMatch,
  validateFeiNumber,
  validateName,
  validatePhone,
  validateUrl,
} from '../../utils/validations';
import {
  CDMO_IMPORT_COLUMNS,
  CDMO_IMPORT_DATA_COLUMNS,
  CDMO_IMPORT_DESCRIPTION_MAX_LENGTH,
  CDMO_IMPORT_EVIDENCE_GROUPS,
  CDMO_IMPORT_EXAMPLE_ROW_NAME,
  CDMO_IMPORT_FACILITY_EQUIPMENT_MAX_LENGTH,
  CDMO_IMPORT_FACILITY_LOCATION_MAX_LENGTH,
  CDMO_IMPORT_MAX_FACILITIES,
  CDMO_IMPORT_MAX_FEI_NUMBERS,
  CDMO_IMPORT_MAX_SECONDARY_EMAILS,
  CDMO_IMPORT_REVIEW_COLUMNS,
  CDMO_IMPORT_STRICT_GROUPS,
  COLLECTED_VIA_OPTIONS,
  DETAILED_CAPABILITY_COLUMNS,
  EVIDENCE_GROUP_DATA_COLUMNS,
  EVIDENCE_STATUS,
  EVIDENCE_GROUP_LABELS,
  EVIDENCE_STATUS_OPTIONS,
  FACILITY_JSON_KEYS,
  columnLabel,
  evidenceColumn,
  statusColumn,
  type CdmoImportDataColumn,
  type CdmoImportEvidenceGroup,
  type DetailedCapabilityColumn,
  type EvidenceStatus,
} from './columns';
import {
  dedupe,
  foldForMatching,
  isIsoDate,
  isPlaceholder,
  matchOption,
  matchOptionIgnoringSpaces,
  sanitizeText,
  splitMultiValue,
} from './normalize';
import type { CdmoImportRawRow } from './parseCsv';

export type CdmoImportRowState = 'ready' | 'warning' | 'error' | 'duplicate';

/** A facility from the `facilities` JSON, validated; its `id` is added when the profile is built. */
export interface CdmoImportFacility {
  region: string;
  location: string;
  capabilities: string[];
  equipment: string;
}

/** Canonical profile values parsed from columns 1–28. Blank cells are '' / [] / null. */
export interface CdmoImportProfile {
  company_name: string;
  website: string;
  /** null when blank — the row builder then applies its default. */
  cdmo_business_profile: CDMOBusinessProfile | null;
  contact_name: string;
  email: string;
  phone: string;
  secondary_emails: string[];
  fei_numbers: string[];
  parent_company: string;
  description: string;
  primary_capabilities: string[];
  detailed_capabilities: Record<string, string[]>;
  typical_batch_sizes: string[];
  budget_ranges: string[];
  target_client_types: string[];
  engagement_scales: string[];
  preferred_project_types: string[];
  gmp_certifications: string[];
  operating_regions: string[];
  facilities: CdmoImportFacility[];
  speed_to_start: string | null;
  /** null when blank ("not verified"); saved as false. */
  willingness_feasibility_studies: boolean | null;
  startup_friendly: boolean | null;
  admin_notes: string;
}

export interface CdmoImportEvidence {
  group: CdmoImportEvidenceGroup;
  /** Canonical status, or null when the cell is blank or not a recognised status. */
  status: EvidenceStatus | null;
  evidence: string;
}

export interface CdmoImportReview {
  research_date: string;
  collected_via: string;
  review_summary: string;
  proposed_changes: string;
  evidence: CdmoImportEvidence[];
}

export interface CdmoImportDuplicate {
  /** `existing`: a profile already in the database; `in_file`: an earlier row in this upload. */
  kind: 'existing' | 'in_file';
  existingId?: string;
  existingName?: string;
  earlierRowNumber?: number;
}

export interface ValidatedCdmoImportRow {
  rowNumber: number;
  /** The row's cells exactly as uploaded, keyed by header. */
  raw: Record<string, string>;
  companyName: string;
  website: string;
  /** Domain used for duplicate detection; null when the website is missing or invalid. */
  normalizedDomain: string | null;
  profile: CdmoImportProfile;
  review: CdmoImportReview;
  /** Review columns (29–66) keyed by header, trimmed and with placeholder words cleared; kept in the audit record (`raw` keeps the original cells). */
  reviewData: Record<string, string>;
  errors: string[];
  warnings: string[];
  duplicate: CdmoImportDuplicate | null;
  state: CdmoImportRowState;
  /** The row had the wrong number of cells, so its values sit under the wrong columns. */
  malformed: boolean;
}

/** An existing profile a row's website domain collides with; `normalized_domain` is the domain of its current website. */
export interface ExistingCdmoDomainMatch {
  id: string;
  company_name: string;
  normalized_domain: string;
}

/** An existing profile holding FEI numbers, stored the way the form stores them (comma-separated). */
export interface ExistingCdmoFeiMatch {
  id: string;
  company_name: string;
  fei_number: string | null;
}

/** The individual FEI numbers in a stored `fei_number` value. */
export const splitStoredFeiNumbers = (value: string | null | undefined): string[] =>
  (value ?? '').split(',').map((fei) => fei.trim()).filter(Boolean);

const MAX_VALUE_IN_MESSAGE = 80;
/** Option lists up to this long are spelled out in the error, so the fix is obvious. */
const MAX_OPTIONS_LISTED = 8;

const quote = (value: string) =>
  `"${value.length > MAX_VALUE_IN_MESSAGE ? `${value.slice(0, MAX_VALUE_IN_MESSAGE)}…` : value}"`;

const allowed = (options: readonly string[]) =>
  options.length <= MAX_OPTIONS_LISTED ? ` Allowed: ${options.map((o) => `"${o}"`).join(', ')}.` : '';

const withArticle = (noun: string) => `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;

/**
 * A duplicate is reported as Duplicate even when the row also has errors: the CDMO already exists, so
 * fixing the cells would not make the row importable. Its errors are still listed in the row detail
 * and the error report. A re-uploaded CDMO almost always has some — its FEI numbers are on the
 * existing profile too.
 */
export function computeRowState(
  errors: readonly string[],
  warnings: readonly string[],
  duplicate: CdmoImportDuplicate | null
): CdmoImportRowState {
  if (duplicate) return 'duplicate';
  if (errors.length > 0) return 'error';
  return warnings.length > 0 ? 'warning' : 'ready';
}

/** Rows in these states are imported unless the admin unticks them. */
export const isImportableState = (state: CdmoImportRowState) => state === 'ready' || state === 'warning';

class RowIssues {
  readonly errors: string[] = [];
  readonly warnings: string[] = [];
  error(message: string) {
    this.errors.push(message);
  }
  warn(message: string) {
    this.warnings.push(message);
  }
}

/**
 * Trims each of `columns` and clears placeholder words ("N/A", "null", "Unknown", …) with a warning,
 * so the rest of validation only ever sees real values or ''. A required column cleared this way is
 * then reported as blank.
 */
function clearPlaceholders(columns: readonly string[], values: Record<string, string>, issues: RowIssues): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const column of columns) {
    const value = (values[column] ?? '').trim();
    if (value && isPlaceholder(value)) {
      issues.warn(`${columnLabel(column)}: ${quote(value)} was treated as blank`);
      cleaned[column] = '';
    } else {
      cleaned[column] = value;
    }
  }
  return cleaned;
}

function cleanDataCells(values: Record<string, string>, issues: RowIssues): Record<CdmoImportDataColumn, string> {
  return clearPlaceholders(CDMO_IMPORT_DATA_COLUMNS, values, issues) as Record<CdmoImportDataColumn, string>;
}

/** Multi-value cell → values, dropping placeholder items with a warning. */
function multiValues(column: string, cell: string, issues: RowIssues): string[] {
  return splitMultiValue(cell).filter((value) => {
    if (isPlaceholder(value)) {
      issues.warn(`${columnLabel(column)}: ${quote(value)} was treated as blank`);
      return false;
    }
    return true;
  });
}

/**
 * Resolves each value of a choice column to its canonical option, reporting the ones that match none.
 * `allowedText` replaces the usual list of options in that message.
 */
function choiceValues(
  column: string,
  cell: string,
  resolve: (value: string) => string | null,
  optionNoun: string,
  options: readonly string[],
  issues: RowIssues,
  allowedText: string = allowed(options)
): string[] {
  const resolved: string[] = [];
  for (const value of multiValues(column, cell, issues)) {
    const canonical = resolve(value);
    if (canonical) {
      resolved.push(canonical);
    } else {
      issues.error(`${columnLabel(column)}: ${quote(value)} is not ${withArticle(optionNoun)}.${allowedText}`);
    }
  }
  return dedupe(resolved);
}

const byOption = (options: readonly string[]) => (value: string) => matchOption(value, options);

/**
 * Every accepted wording of every batch size, for the error message: each size may be written in kg,
 * in litres or both, so all 15 are listed, size by size.
 */
export const BATCH_SIZE_ALLOWED_TEXT = ` Allowed (each size in kg, in litres or both): ${BATCH_SIZES_KG_OPTIONS.map(
  (kg, index) => `"${kg}", "${BATCH_SIZES_L_OPTIONS[index]}" or "${BATCH_SIZES_COMBINED_OPTIONS[index]}"`
).join('; ')}.`;

/** Batch sizes are stored as kg labels; the litre and combined wordings map to them by position. */
function resolveBatchSize(value: string): string | null {
  const kg = matchOptionIgnoringSpaces(value, BATCH_SIZES_KG_OPTIONS);
  if (kg) return kg;
  for (const alternative of [BATCH_SIZES_L_OPTIONS, BATCH_SIZES_COMBINED_OPTIONS]) {
    const match = matchOptionIgnoringSpaces(value, alternative);
    if (match) return BATCH_SIZES_KG_OPTIONS[alternative.indexOf(match)] ?? null;
  }
  return null;
}

function resolveProjectType(value: string): string | null {
  const legacyKey = Object.keys(LEGACY_SUPPORT_SERVICE_ALIASES).find(
    (key) => foldForMatching(key) === foldForMatching(value)
  );
  return matchOption(legacyKey ? LEGACY_SUPPORT_SERVICE_ALIASES[legacyKey] : value, PROJECT_TYPES_OPTIONS);
}

/**
 * Short names research output uses for a certification whose option carries a condition. Kept here
 * rather than in the shared alias table, which also feeds the matching engine's exact regulatory gate;
 * the import saves the full option, so the gate still only ever sees exact labels.
 */
const IMPORT_CERTIFICATION_SHORT_NAMES: Readonly<Record<string, string>> = {
  'ISO 13485': 'ISO 13485, if device or combination-product related',
};

/** Legacy labels are matched like current ones — case, spacing and dashes ignored — so "ema gmp" is "EU GMP". */
function resolveCertification(value: string): string | null {
  const aliases = { ...LEGACY_CERTIFICATION_ALIASES, ...IMPORT_CERTIFICATION_SHORT_NAMES };
  const legacyKey = Object.keys(aliases).find((key) => foldForMatching(key) === foldForMatching(value));
  return matchOption(legacyKey ? aliases[legacyKey] : value, GMP_CERTIFICATIONS_OPTIONS);
}

function resolveCapability(value: string, options: readonly string[]): string | null {
  return matchOption(resolveDetailedCapabilityLabel(value), options) ?? matchOption(value, options);
}

function parseYesNo(column: string, cell: string, issues: RowIssues): boolean | null {
  if (!cell) {
    issues.warn(`${columnLabel(column)}: blank — saved as "No" and noted in admin notes as not verified`);
    return null;
  }
  const folded = foldForMatching(cell);
  if (folded === 'yes') return true;
  if (folded === 'no') return false;
  issues.error(`${columnLabel(column)}: ${quote(cell)} must be "Yes", "No" or blank`);
  return null;
}

/**
 * Curly apostrophes as straight ones. Names copied from web pages often have "O’Neill", which the
 * shared name and company checks (also the profile form's) refuse, while "O'Neill" passes.
 */
const straightApostrophes = (value: string) => value.replace(/[\u2018\u2019\u02BC]/g, "'");

/** How a JSON null or a placeholder word is quoted in a "treated as blank" warning. */
const blankWord = (value: string | null) => (value === null ? 'null' : quote(value));

function parseFacilities(
  cell: string,
  operatingRegions: readonly string[],
  detailedCapabilities: Record<string, string[]>,
  issues: RowIssues
): CdmoImportFacility[] {
  if (!cell) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(cell);
  } catch (error) {
    // Browsers word JSON errors differently; most give a character position, which is what helps.
    const position = /position (\d+)/.exec(error instanceof Error ? error.message : '')?.[1];
    issues.error(
      `Facilities: not valid JSON — check the quotes, commas and brackets${position ? ` (near character ${Number(position) + 1})` : ''}`
    );
    return [];
  }
  if (!Array.isArray(parsed)) {
    issues.error('Facilities: must be a JSON list of facilities, e.g. [{"region": …}]');
    return [];
  }
  if (parsed.length > CDMO_IMPORT_MAX_FACILITIES) {
    issues.error(`Facilities: ${parsed.length} facilities — the limit is ${CDMO_IMPORT_MAX_FACILITIES}`);
    return [];
  }

  const profileCapabilities = dedupe(Object.values(detailedCapabilities).flat());
  const facilities: CdmoImportFacility[] = [];

  parsed.forEach((item: unknown, index) => {
    const label = `Facility ${index + 1}`;
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      issues.error(`${label}: must be an object with region, location, capabilities and equipment`);
      return;
    }
    const record = item as Record<string, unknown>;
    const keys = Object.keys(record);
    const extra = keys.filter((key) => !(FACILITY_JSON_KEYS as readonly string[]).includes(key));
    const missing = FACILITY_JSON_KEYS.filter((key) => !keys.includes(key));
    if (extra.length) {
      issues.error(`${label}: unexpected field ${extra.map((k) => `"${k}"`).join(', ')} — only region, location, capabilities and equipment are allowed`);
    }
    if (missing.length) issues.error(`${label}: missing field ${missing.map((k) => `"${k}"`).join(', ')}`);

    let region = '';
    if (typeof record.region !== 'string' || !record.region.trim() || isPlaceholder(record.region)) {
      issues.error(`${label}: region is required`);
    } else {
      const canonicalRegion = matchOption(record.region, OPERATING_REGIONS_OPTIONS);
      if (!canonicalRegion) {
        issues.error(`${label}: region ${quote(record.region)} is not an operating region option.${allowed(OPERATING_REGIONS_OPTIONS)}`);
      } else if (!operatingRegions.includes(canonicalRegion)) {
        issues.error(`${label}: region ${quote(canonicalRegion)} is not one of this row's operating regions`);
      } else {
        region = canonicalRegion;
      }
    }

    const capabilities: string[] = [];
    if (record.capabilities === null) {
      issues.warn(`${label}: capabilities null was treated as an empty list`);
    } else if (!Array.isArray(record.capabilities)) {
      issues.error(`${label}: capabilities must be a list of capability names`);
    } else {
      for (const capability of record.capabilities) {
        if (capability === null || (typeof capability === 'string' && isPlaceholder(capability))) {
          issues.warn(`${label}: capability ${blankWord(capability)} was treated as blank`);
          continue;
        }
        if (typeof capability !== 'string') {
          issues.error(`${label}: capabilities must contain only text`);
          continue;
        }
        if (!capability.trim()) continue;
        const canonical = resolveCapability(capability.trim(), profileCapabilities);
        if (canonical) {
          capabilities.push(canonical);
        } else {
          issues.error(`${label}: capability ${quote(capability.trim())} is not one of this row's detailed capabilities`);
        }
      }
    }

    const text = (field: 'location' | 'equipment', maxLength: number): string => {
      const value = record[field];
      if (value === undefined) return '';
      if (value === null || (typeof value === 'string' && isPlaceholder(value))) {
        issues.warn(`${label}: ${field} ${blankWord(value)} was treated as blank`);
        return '';
      }
      if (typeof value !== 'string') {
        issues.error(`${label}: ${field} must be text`);
        return '';
      }
      const clean = sanitizeText(value);
      if (clean.length > maxLength) {
        issues.error(`${label}: ${field} is ${clean.length} characters — the limit is ${maxLength}`);
      }
      return clean;
    };

    facilities.push({
      region,
      location: text('location', CDMO_IMPORT_FACILITY_LOCATION_MAX_LENGTH),
      capabilities: dedupe(capabilities),
      equipment: text('equipment', CDMO_IMPORT_FACILITY_EQUIPMENT_MAX_LENGTH),
    });
  });

  return facilities;
}

function parseProfile(cells: Record<CdmoImportDataColumn, string>, issues: RowIssues): CdmoImportProfile {
  // company_name
  const companyName = straightApostrophes(sanitizeText(cells.company_name));
  if (CDMO_IMPORT_EXAMPLE_ROW_NAME.test(companyName)) {
    issues.error(`Company name: ${quote(companyName)} is the template's example row — delete this row before uploading`);
  } else {
    const check = validateCompany(companyName);
    if (!check.isValid) issues.error(companyName ? `Company name: ${quote(companyName)} — ${check.error}` : 'Company name: required');
  }

  // website
  const website = cells.website;
  const websiteCheck = validateUrl(website, true);
  if (!websiteCheck.isValid) issues.error(website ? `Website: ${quote(website)} — ${websiteCheck.error}` : 'Website: required');

  // cdmo_business_profile
  let businessProfile: CDMOBusinessProfile | null = null;
  if (!cells.cdmo_business_profile) {
    issues.warn('Business profile: blank — saved as "Mid-Size Specialist"');
  } else {
    const match = matchOption(cells.cdmo_business_profile, CDMO_BUSINESS_PROFILE_OPTIONS);
    if (match) {
      businessProfile = match as CDMOBusinessProfile;
    } else {
      issues.error(
        `Business profile: ${quote(cells.cdmo_business_profile)} is not a business profile option.${allowed(CDMO_BUSINESS_PROFILE_OPTIONS)}`
      );
    }
  }

  // contact_name
  const contactName = straightApostrophes(sanitizeText(cells.contact_name));
  if (contactName) {
    const check = validateName(contactName);
    if (!check.isValid) issues.error(`Contact name: ${quote(contactName)} — ${check.error}`);
  }

  // email → primary_email, the claim contact. An address on another domain than the website (a
  // subsidiary's, for example) is imported with a warning so the admin checks it is the company's; the
  // profile form and the claim page accept the addresses saved on a profile.
  const email = cells.email.toLowerCase();
  const offWebsiteDomain = (column: string, address: string) => {
    const domainCheck = websiteCheck.isValid ? validateEmailDomainMatch(address, website) : { isValid: true };
    if (!domainCheck.isValid) {
      issues.warn(
        `${columnLabel(column)}: ${quote(address)} is not on the website's domain (${normalizeWebsiteDomain(website)}) — imported as it is, and as a saved contact it will be able to claim this profile; check it is the company's own address, e.g. a subsidiary's`
      );
    }
  };
  if (email) {
    const check = validateEmail(email);
    if (check.isValid) offWebsiteDomain('email', email);
    else issues.error(`Email: ${quote(email)} — ${check.error}`);
  } else {
    issues.warn('Email: blank — this profile cannot be sent claim emails until an email is added');
  }

  // phone
  let phone = cells.phone;
  if (phone) {
    // "+44 (0)20 …": the "(0)" is the trunk prefix used only inside the country, never after the
    // country code. Dropped, with a warning, so the number is not saved with a wrong extra 0.
    const trunkPrefix = /^(\+\d{1,3})\s*\(0\)\s*/;
    const written = phone;
    if (trunkPrefix.test(phone)) phone = phone.replace(trunkPrefix, '$1 ');
    let check = validatePhone(phone);
    // The shared check (also the profile form's) takes spaced numbers only in the US, UK and Indian
    // layouts, so "+41 34 424 41 11" fails although "+41344244111" passes. A number with its country
    // code is saved in that compact form, which the form also accepts on the next edit.
    if (!check.isValid && /^\+\d[\d\s().-]*$/.test(phone)) {
      const compact = `+${phone.replace(/\D/g, '')}`;
      const retry = validatePhone(compact);
      if (retry.isValid) {
        phone = compact;
        check = retry;
      }
    }
    if (check.isValid) {
      // The shared check formats every 10-digit number the US way, dropping the country code
      // ("(497) 392-7010" for "+49 7392 7010", "(617) 555-0142" for "+1 617 555 0142"). A number
      // written with its country code keeps it, as the profile form saves it; only one without
      // takes the US form.
      if (check.formatted && !phone.startsWith('+')) phone = check.formatted;
      if (trunkPrefix.test(written)) {
        issues.warn(`Phone: ${quote(written)} saved as ${quote(phone)} — the "(0)" after the country code was removed; check the number`);
      }
    } else {
      issues.error(`Phone: ${quote(written)} — ${check.error}`);
    }
  }

  // secondary_emails
  const secondaryEmails = dedupe(multiValues('secondary_emails', cells.secondary_emails, issues).map((e) => e.toLowerCase()));
  if (secondaryEmails.length > CDMO_IMPORT_MAX_SECONDARY_EMAILS) {
    issues.error(`Secondary emails: ${secondaryEmails.length} addresses — the limit is ${CDMO_IMPORT_MAX_SECONDARY_EMAILS}`);
  }
  for (const secondary of secondaryEmails) {
    const check = validateEmail(secondary);
    if (!check.isValid) {
      issues.error(`Secondary emails: ${quote(secondary)} — ${check.error}`);
    } else if (secondary === email) {
      issues.error(`Secondary emails: ${quote(secondary)} is the same as the main email — additional emails must be different`);
    } else {
      offWebsiteDomain('secondary_emails', secondary);
    }
  }

  // fei_number
  const feiNumbers = multiValues('fei_number', cells.fei_number, issues);
  if (feiNumbers.length > CDMO_IMPORT_MAX_FEI_NUMBERS) {
    issues.error(`FEI number: ${feiNumbers.length} numbers — the limit is ${CDMO_IMPORT_MAX_FEI_NUMBERS}`);
  }
  for (const fei of feiNumbers) {
    const check = validateFeiNumber(fei);
    if (!check.isValid) issues.error(`FEI number: ${quote(fei)} — ${check.error}`);
  }

  // description
  const description = sanitizeText(cells.description);
  if (description.length > CDMO_IMPORT_DESCRIPTION_MAX_LENGTH) {
    issues.error(
      `Description: ${description.length} characters — the limit is ${CDMO_IMPORT_DESCRIPTION_MAX_LENGTH.toLocaleString('en-US')}`
    );
  }

  // primary_capabilities
  const errorsBeforePrimary = issues.errors.length;
  const primaryCapabilities = choiceValues(
    'primary_capabilities',
    cells.primary_capabilities,
    byOption(PRIMARY_CAPABILITIES_OPTIONS),
    'primary modality option',
    PRIMARY_CAPABILITIES_OPTIONS,
    issues
  );
  if (primaryCapabilities.length === 0 && issues.errors.length === errorsBeforePrimary) {
    issues.error('Primary modalities: required — list at least one');
  }

  // capabilities_<modality> → detailed_capabilities
  const detailedCapabilities: Record<string, string[]> = {};
  for (const column of Object.keys(DETAILED_CAPABILITY_COLUMNS) as DetailedCapabilityColumn[]) {
    const modality = DETAILED_CAPABILITY_COLUMNS[column];
    const values = multiValues(column, cells[column], issues);
    if (values.length === 0) continue;
    if (!primaryCapabilities.includes(modality)) {
      issues.error(`${columnLabel(column)}: has values but "${modality}" is not listed in Primary modalities`);
    }
    const options = getDetailedCapabilityOptions(modality);
    const resolved: string[] = [];
    for (const value of values) {
      const canonical = resolveCapability(value, options);
      if (canonical) {
        resolved.push(canonical);
      } else {
        issues.error(`${columnLabel(column)}: ${quote(value)} is not a ${modality} option`);
      }
    }
    if (resolved.length) detailedCapabilities[modality] = dedupe(resolved);
  }

  const typicalBatchSizes = choiceValues(
    'typical_batch_sizes', cells.typical_batch_sizes, resolveBatchSize, 'typical batch size option', BATCH_SIZES_KG_OPTIONS, issues,
    BATCH_SIZE_ALLOWED_TEXT
  );
  const budgetRanges = choiceValues(
    'budget_ranges', cells.budget_ranges, (value) => matchOptionIgnoringSpaces(value, BUDGET_RANGES_OPTIONS), 'budget range option', BUDGET_RANGES_OPTIONS, issues
  );
  const targetClientTypes = choiceValues(
    'target_client_types', cells.target_client_types, byOption(CLIENT_TYPES_OPTIONS), 'target client type option', CLIENT_TYPES_OPTIONS, issues
  );
  const engagementScales = choiceValues(
    'engagement_scales', cells.engagement_scales, byOption(ENGAGEMENT_SCALES_OPTIONS), 'engagement scale option', ENGAGEMENT_SCALES_OPTIONS, issues
  );
  const preferredProjectTypes = choiceValues(
    'preferred_project_types', cells.preferred_project_types, resolveProjectType, 'project type option', PROJECT_TYPES_OPTIONS, issues
  );
  const gmpCertifications = choiceValues(
    'gmp_certifications', cells.gmp_certifications, resolveCertification, 'GMP certification option', GMP_CERTIFICATIONS_OPTIONS, issues
  );
  const operatingRegions = choiceValues(
    'operating_regions', cells.operating_regions, byOption(OPERATING_REGIONS_OPTIONS), 'operating region option', OPERATING_REGIONS_OPTIONS, issues
  );

  // The three lists the profile form requires but research may not find. The profile is still
  // created; whoever next saves it through the form will be asked to fill them in.
  const blankRequiredLists: Array<[string, string, string[]]> = [
    ['typical_batch_sizes', cells.typical_batch_sizes, typicalBatchSizes],
    ['budget_ranges', cells.budget_ranges, budgetRanges],
    ['operating_regions', cells.operating_regions, operatingRegions],
  ];
  for (const [column, cell, values] of blankRequiredLists) {
    if (values.length === 0 && splitMultiValue(cell).every(isPlaceholder)) {
      issues.warn(`${columnLabel(column)}: blank — must be filled in the next time this profile is edited`);
    }
  }

  const facilities = parseFacilities(cells.facilities, operatingRegions, detailedCapabilities, issues);

  // speed_to_start
  let speedToStart: string | null = null;
  if (cells.speed_to_start) {
    speedToStart = matchOption(cells.speed_to_start, SPEED_TO_START_OPTIONS);
    if (!speedToStart) {
      issues.error(`Speed to start: ${quote(cells.speed_to_start)} is not a speed to start option.${allowed(SPEED_TO_START_OPTIONS)}`);
    }
  }

  return {
    company_name: companyName,
    website,
    cdmo_business_profile: businessProfile,
    contact_name: contactName,
    email,
    phone,
    secondary_emails: secondaryEmails,
    fei_numbers: feiNumbers,
    parent_company: sanitizeText(cells.parent_company),
    description,
    primary_capabilities: primaryCapabilities,
    detailed_capabilities: detailedCapabilities,
    typical_batch_sizes: typicalBatchSizes,
    budget_ranges: budgetRanges,
    target_client_types: targetClientTypes,
    engagement_scales: engagementScales,
    preferred_project_types: preferredProjectTypes,
    gmp_certifications: gmpCertifications,
    operating_regions: operatingRegions,
    facilities,
    speed_to_start: speedToStart,
    willingness_feasibility_studies: parseYesNo('willingness_feasibility_studies', cells.willingness_feasibility_studies, issues),
    startup_friendly: parseYesNo('startup_friendly', cells.startup_friendly, issues),
    admin_notes: sanitizeText(cells.admin_notes),
  };
}

function parseReview(
  values: Record<string, string>,
  cells: Record<CdmoImportDataColumn, string>,
  issues: RowIssues
): { review: CdmoImportReview; reviewData: Record<string, string> } {
  const reviewData = clearPlaceholders(CDMO_IMPORT_REVIEW_COLUMNS, values, issues);

  const researchDate = reviewData.research_date;
  if (!researchDate) {
    issues.error('Research date: required — the date the research was done, as YYYY-MM-DD');
  } else if (!isIsoDate(researchDate)) {
    issues.error(`Research date: ${quote(researchDate)} must be a real date written as YYYY-MM-DD`);
  }

  let collectedVia = reviewData.collected_via;
  if (!collectedVia) {
    issues.error(`Collected via: required.${allowed(COLLECTED_VIA_OPTIONS)}`);
  } else {
    const match = matchOption(collectedVia, COLLECTED_VIA_OPTIONS);
    if (match) {
      collectedVia = match;
    } else {
      issues.error(`Collected via: ${quote(collectedVia)} is not a collection method.${allowed(COLLECTED_VIA_OPTIONS)}`);
    }
  }

  if (!reviewData.review_summary) {
    issues.error('Review summary: required — summarise what was verified and what was not');
  }

  const evidence = CDMO_IMPORT_EVIDENCE_GROUPS.map((group): CdmoImportEvidence => {
    const statusCol = statusColumn(group);
    const evidenceCol = evidenceColumn(group);
    const statusRaw = reviewData[statusCol];
    const evidenceText = reviewData[evidenceCol];

    let status: EvidenceStatus | null = null;
    if (statusRaw) {
      status = matchOption(statusRaw, EVIDENCE_STATUS_OPTIONS) as EvidenceStatus | null;
      if (!status) {
        issues.error(`${columnLabel(statusCol)}: ${quote(statusRaw)} is not a status.${allowed(EVIDENCE_STATUS_OPTIONS)}`);
      }
    }

    if (
      status &&
      CDMO_IMPORT_STRICT_GROUPS.has(group) &&
      (status === EVIDENCE_STATUS.INFERRED || status === EVIDENCE_STATUS.MIXED)
    ) {
      issues.error(
        `${columnLabel(statusCol)}: "${status}" is not allowed — ${EVIDENCE_GROUP_LABELS[group].toLowerCase()} must be "${EVIDENCE_STATUS.VERIFIED}" or "${EVIDENCE_STATUS.UNKNOWN}"`
      );
    }

    if (status && status !== EVIDENCE_STATUS.UNKNOWN && !evidenceText) {
      issues.error(`${columnLabel(evidenceCol)}: required when the status is "${status}"`);
    }

    const hasData = EVIDENCE_GROUP_DATA_COLUMNS[group].some((column) => cells[column] !== '');
    if (hasData && status === EVIDENCE_STATUS.UNKNOWN) {
      issues.warn(`${EVIDENCE_GROUP_LABELS[group]}: has data but its status is "${EVIDENCE_STATUS.UNKNOWN}"`);
    } else if (hasData && !statusRaw) {
      issues.warn(`${EVIDENCE_GROUP_LABELS[group]}: has data but no status (treated as "${EVIDENCE_STATUS.UNKNOWN}")`);
    }

    return { group, status, evidence: evidenceText };
  });

  return {
    review: {
      research_date: researchDate,
      collected_via: collectedVia,
      review_summary: reviewData.review_summary,
      proposed_changes: reviewData.proposed_changes,
      evidence,
    },
    reviewData,
  };
}

/**
 * Validates and normalises one uploaded row. Pure: duplicate detection needs the database and the
 * other rows, so it is applied afterwards by {@link markDuplicates}.
 */
export function validateRow(row: CdmoImportRawRow): ValidatedCdmoImportRow {
  const issues = new RowIssues();

  // A row with the wrong number of cells has its values under the wrong headers, so every other
  // message about it would be misleading. Report the one real problem; still parse it (into a
  // discarded issue list) so the preview can show what was read.
  const structurallyBroken = row.cellCount !== CDMO_IMPORT_COLUMNS.length;
  if (structurallyBroken) {
    issues.error(
      `This row has ${row.cellCount} cells but the template has ${CDMO_IMPORT_COLUMNS.length}, so its values are under the wrong columns and nothing else in it was checked. Look for a missing or extra comma, or a value containing a comma that is not in quotes.`
    );
  }
  const fieldIssues = structurallyBroken ? new RowIssues() : issues;

  const cells = cleanDataCells(row.values, fieldIssues);
  const profile = parseProfile(cells, fieldIssues);
  const { review, reviewData } = parseReview(row.values, cells, fieldIssues);

  const normalizedDomain = validateUrl(profile.website, true).isValid ? normalizeWebsiteDomain(profile.website) : null;

  return {
    rowNumber: row.rowNumber,
    raw: row.values,
    companyName: profile.company_name,
    website: profile.website,
    normalizedDomain,
    profile,
    review,
    reviewData,
    errors: issues.errors,
    warnings: issues.warnings,
    duplicate: null,
    state: computeRowState(issues.errors, issues.warnings, null),
    malformed: structurallyBroken,
  };
}

/**
 * Flags rows whose website domain already belongs to a profile, or to an earlier row in the file
 * that will be imported (a row with errors keeps nothing, so its corrected copy is imported). Updating
 * an existing profile from a CSV is out of scope, so a duplicate is never imported.
 */
export function markDuplicates(
  rows: readonly ValidatedCdmoImportRow[],
  existing: readonly ExistingCdmoDomainMatch[]
): ValidatedCdmoImportRow[] {
  const existingByDomain = new Map<string, ExistingCdmoDomainMatch>();
  for (const match of existing) {
    if (match.normalized_domain && !existingByDomain.has(match.normalized_domain)) {
      existingByDomain.set(match.normalized_domain, match);
    }
  }

  const firstRowByDomain = new Map<string, number>();
  return rows.map((row) => {
    let duplicate: CdmoImportDuplicate | null = null;
    const domain = row.malformed ? null : row.normalizedDomain;
    if (domain) {
      const inDatabase = existingByDomain.get(domain);
      const earlierRow = firstRowByDomain.get(domain);
      if (inDatabase) {
        duplicate = { kind: 'existing', existingId: inDatabase.id, existingName: inDatabase.company_name };
      } else if (earlierRow !== undefined) {
        duplicate = { kind: 'in_file', earlierRowNumber: earlierRow };
      }
      if (!duplicate && row.errors.length === 0) firstRowByDomain.set(domain, row.rowNumber);
    }
    return { ...row, duplicate, state: computeRowState(row.errors, row.warnings, duplicate) };
  });
}

/**
 * An FEI number identifies one FDA-registered site, so it may belong to only one profile, as on the
 * profile form. A row's FEI numbers are compared exactly with existing profiles and earlier rows; one
 * with errors or its own clash holds none, but a duplicate does, as its CDMO already exists.
 */
export function markFeiConflicts(
  rows: readonly ValidatedCdmoImportRow[],
  existing: readonly ExistingCdmoFeiMatch[]
): ValidatedCdmoImportRow[] {
  const holderByFei = new Map<string, ExistingCdmoFeiMatch>();
  for (const match of existing) {
    for (const fei of splitStoredFeiNumbers(match.fei_number)) {
      if (!holderByFei.has(fei)) holderByFei.set(fei, match);
    }
  }

  const firstRowByFei = new Map<string, number>();
  return rows.map((row) => {
    if (row.malformed) return row;
    const conflicts: string[] = [];
    for (const fei of row.profile.fei_numbers) {
      const holder = holderByFei.get(fei);
      const earlierRow = firstRowByFei.get(fei);
      if (holder) {
        conflicts.push(
          `FEI number: "${fei}" is already registered to the existing profile "${holder.company_name.trim()}" — an FEI number can belong to only one profile`
        );
      } else if (earlierRow !== undefined) {
        conflicts.push(`FEI number: "${fei}" is also listed on row ${earlierRow} of this file — an FEI number can belong to only one profile`);
      }
    }
    if (conflicts.length === 0 && row.errors.length === 0) for (const fei of row.profile.fei_numbers) firstRowByFei.set(fei, row.rowNumber);
    if (conflicts.length === 0) return row;
    const errors = [...row.errors, ...conflicts];
    return { ...row, errors, state: computeRowState(errors, row.warnings, row.duplicate) };
  });
}

/**
 * Where a row sits in the uploaded file, in both numberings a person may be looking at: rows count
 * CDMOs from the first row under the header, while a spreadsheet also counts the header row.
 */
export function describeRowPosition(rowNumber: number): string {
  return `Row ${rowNumber} (spreadsheet row ${rowNumber + 1})`;
}

/** One-line reason a row is a duplicate, used in the preview and in the error report. */
export function describeDuplicate(row: Pick<ValidatedCdmoImportRow, 'duplicate' | 'normalizedDomain'>): string | null {
  const { duplicate, normalizedDomain } = row;
  if (!duplicate) return null;
  if (duplicate.kind === 'existing') {
    return `Duplicate: website domain "${normalizedDomain}" already belongs to the existing profile "${duplicate.existingName?.trim()}"`;
  }
  return `Duplicate: website domain "${normalizedDomain}" is also used by row ${duplicate.earlierRowNumber} of this file`;
}

/** The same reason in a few words, for the review table. */
export function describeDuplicateShort(row: Pick<ValidatedCdmoImportRow, 'duplicate'>): string | null {
  const { duplicate } = row;
  if (!duplicate) return null;
  return duplicate.kind === 'existing'
    ? `Already exists as "${duplicate.existingName?.trim()}"`
    : `Same website as row ${duplicate.earlierRowNumber}`;
}
